import { useMemo, type JSX } from 'react';
import { ArrowDown } from 'lucide-react';
import { Combobox } from '@/components/ui/combobox';
import { PlatformIcon } from '@/components/PlatformIcon';
import { channelLabel } from '@/components/ui/tag';
import { useFunnel2 } from '@/hooks/useInsights';
import { useUrlState } from '@/hooks/useUrlState';
import { INVITE_CHANNELS } from '@/api/types';
import type { DurationStats, Funnel2Response, FunnelMode, FunnelStage, FunnelStageKey } from '@/api/insights';
import {
  changeOf,
  daysText,
  insightsInvitesHref,
  isThinSample,
  JOINED_STAGES,
  JOINS_DATED_FROM,
  MIN_SAMPLE,
  pctText,
  POSTED_STAGES,
  REPLIED_STAGES,
  windowPredatesJoinDates,
  type DrillOptions,
} from '@/lib/insights';
import { ratio } from '@/lib/chart';
import { formatDate } from '@/lib/dates';
import { creatorTypeLabel } from '@/lib/labels';
import { cn } from '@/lib/utils';
import { useInsightsScope } from './scope';
import {
  ChangeText,
  CountLink,
  LoadError,
  Loading,
  MetricLabel,
  Note,
  Panel,
  RateValue,
  Section,
  Segmented,
  Th,
} from './parts';

const MODES: { value: FunnelMode; label: string }[] = [
  { value: 'cohort', label: 'Follow this period’s replies' },
  { value: 'activity', label: 'What happened this period' },
];

const STAGE_LABELS: Record<FunnelStageKey, string> = {
  messaged: 'Messaged',
  replied: 'Replied',
  joined: 'Joined',
  posted: 'Posted',
};

const FALLBACK_DEFS: Record<string, string> = {
  messages: '“I messaged them” records first made in this period.',
  replied: 'Invites logged in this period, not rejected or hidden.',
  joinedCohort: 'Of this period’s replies, how many have joined since.',
  joinedActivity: 'Joins recorded in this period, whenever the reply was logged.',
  postedCohort: 'Of this period’s replies, how many have posted since.',
  postedActivity: 'First posts recorded in this period, whenever the reply was logged.',
  rateFromStart:
    'Joins (or posts) whose creator had a recorded “I messaged them” first, ÷ messages. Replies logged without a recorded message can’t be traced back to one.',
  timeToJoinDays: 'Days from the reply being logged to the creator joining.',
  timeToPostDays: 'Days from the reply being logged to the first approved post.',
};

export function FunnelTab(): JSX.Element {
  const scope = useInsightsScope();
  const [url, setUrl] = useUrlState({ mode: 'cohort', channel: '' });
  const mode: FunnelMode = url.mode === 'activity' ? 'activity' : 'cohort';
  const channel = (INVITE_CHANNELS as readonly string[]).includes(url.channel) ? url.channel : '';
  const extra = useMemo(() => ({ mode, ...(channel ? { channel } : {}) }), [mode, channel]);
  const current = useFunnel2({ ...scope.params, ...extra });
  const previous = useFunnel2({ ...scope.previousParams, ...extra });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Segmented label="Funnel mode" value={mode} options={MODES} onChange={(v) => setUrl({ mode: v })} />
        <div className="w-full sm:w-44">
          <Combobox
            aria-label="Channel"
            value={channel}
            onChange={(v) => setUrl({ channel: v })}
            options={INVITE_CHANNELS.map((c) => ({ value: c, label: channelLabel(c) }))}
            allowAll
            allLabel="All channels"
            placeholder="All channels"
            className="w-full"
          />
        </div>
      </div>
      <ModeExplainer mode={mode} />

      {current.isLoading ? (
        <Loading label="Loading the funnel…" />
      ) : current.isError || !current.data ? (
        <LoadError error={current.error} what="the funnel" />
      ) : (
        <FunnelBody data={current.data} previous={previous.data ?? null} mode={mode} channel={channel} />
      )}
    </div>
  );
}

function ModeExplainer({ mode }: { mode: FunnelMode }): JSX.Element {
  return (
    <Panel className="text-sm">
      {mode === 'cohort' ? (
        <p>
          <span className="font-semibold">Follow this period’s replies.</span>{' '}
          <span className="text-muted-foreground">
            Starts from the creators who replied in this period and follows them forward: how many have joined and
            posted since, whenever that happened. Use it for conversion rates. A recent period looks weaker than it
            will, because some of its creators haven’t had time to join yet.
          </span>
        </p>
      ) : (
        <p>
          <span className="font-semibold">What happened this period.</span>{' '}
          <span className="text-muted-foreground">
            Counts each step when it happened: messages sent, replies logged, joins and first posts recorded in this
            period, whoever they belong to. Use it for workload and for what the spend bought. The people in each step
            differ, so no rates between steps.
          </span>
        </p>
      )}
    </Panel>
  );
}

function FunnelBody({
  data: d,
  previous,
  mode,
  channel,
}: {
  data: Funnel2Response;
  previous: Funnel2Response | null;
  mode: FunnelMode;
  channel: string;
}): JSX.Element {
  const scope = useInsightsScope();
  const def = (key: string): string | undefined => d.definitions?.[key] ?? FALLBACK_DEFS[key];
  /** Definition key for a stage in this mode (the backend's metric names). */
  const stageDef = (key: FunnelStageKey): string | undefined =>
    def(key === 'messaged' ? 'messages' : key === 'replied' ? 'replied' : `${key}${mode === 'cohort' ? 'Cohort' : 'Activity'}`);
  const window = scope.windows.current;
  const team = scope.team || undefined;
  const max = Math.max(...d.stages.map((s) => s.count), 0);
  const prevBy = new Map((previous?.stages ?? []).map((s) => [s.key, s.count]));
  const excluded = d.excludedUntrackedTeams.map((t) => scope.teamName(t));
  const empty = d.stages.every((s) => s.count === 0);

  const drill = (key: FunnelStageKey, extra: Partial<DrillOptions> = {}): string | null => {
    const base = { team, window, channel: channel || undefined, ...extra };
    switch (key) {
      case 'replied':
        return insightsInvitesHref({ ...base, stages: REPLIED_STAGES });
      case 'joined':
        return insightsInvitesHref({ ...base, stages: JOINED_STAGES, by: mode === 'activity' ? 'joined' : 'logged' });
      case 'posted':
        return insightsInvitesHref({ ...base, stages: POSTED_STAGES, by: mode === 'activity' ? 'posted' : 'logged' });
      default:
        return null; // Messages have no invite behind them.
    }
  };

  return (
    <div className="space-y-6">
      <Section
        title={mode === 'cohort' ? 'Where do this period’s creators drop out?' : 'How much moved through each step this period?'}
        description={
          mode === 'cohort'
            ? 'Each step as a share of the step before it, and of everyone messaged.'
            : 'Counts of what happened in the period, with the change on the period before.'
        }
      >
        {empty ? (
          <Panel>
            <p className="text-sm text-muted-foreground">Nothing recorded in this period for this scope.</p>
          </Panel>
        ) : (
          <Panel>
            <ol className="space-y-1">
              {d.stages.map((s, i) => (
                <li key={s.key}>
                  {i > 0 ? (
                    <StepConnector
                      stage={s}
                      mode={mode}
                      previousLabel={STAGE_LABELS[d.stages[i - 1]!.key]}
                      fromStartDef={def('rateFromStart')}
                    />
                  ) : null}
                  <StageBar
                    stage={s}
                    max={max}
                    def={stageDef(s.key)}
                    to={drill(s.key)}
                    change={changeOf(s.count, prevBy.has(s.key) ? prevBy.get(s.key)! : null)}
                  />
                </li>
              ))}
            </ol>
          </Panel>
        )}
        {mode === 'activity' && windowPredatesJoinDates(window) ? (
          <Note tone="warning">
            Joins are only dated from {formatDate(`${JOINS_DATED_FROM}T12:00:00Z`)}. Joins before that aren’t counted in
            this view; switch to “Follow this period’s replies” for them.
          </Note>
        ) : null}
        {excluded.length > 0 ? (
          <Note>
            This funnel covers tracked teams only. {excluded.join(' and ')} {excluded.length === 1 ? 'is' : 'are'} left
            out: {excluded.length === 1 ? 'its' : 'their'} joins and posts are updated by hand, so{' '}
            {excluded.length === 1 ? 'it' : 'they'} would read as never converting.
          </Note>
        ) : null}
      </Section>

      <div className="grid gap-3 sm:grid-cols-2">
        <DurationTile label="Time from reply to join" def={def('timeToJoinDays')} stats={d.timeToJoinDays} />
        <DurationTile label="Time from reply to first post" def={def('timeToPostDays')} stats={d.timeToPostDays} />
      </div>

      <ChannelTable rows={d.byChannel} mode={mode} def={def} stageDef={stageDef} drill={drill} />
      <CreatorTypeTable rows={d.byCreatorType} mode={mode} def={def} stageDef={stageDef} />
    </div>
  );
}

function StageBar({
  stage,
  max,
  def,
  to,
  change,
}: {
  stage: FunnelStage;
  max: number;
  def?: string;
  to: string | null;
  change: ReturnType<typeof changeOf>;
}): JSX.Element {
  const width = max > 0 ? (stage.count / max) * 100 : 0;
  return (
    <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-3 sm:grid-cols-[8rem_minmax(0,1fr)_7rem]">
      <span className="text-sm font-medium">
        <MetricLabel label={STAGE_LABELS[stage.key]} def={def} />
      </span>
      <div className="h-7 overflow-hidden rounded-md bg-muted">
        <div
          className="flex h-full items-center rounded-md bg-brand/80 px-2"
          style={{ width: `${Math.max(stage.count > 0 ? 2 : 0, width)}%` }}
        />
      </div>
      <span className="col-start-2 flex items-baseline justify-end gap-2 text-right sm:col-start-3">
        <CountLink value={stage.count} to={to} className="text-base" />
        <ChangeText change={change} />
      </span>
    </div>
  );
}

function StepConnector({
  stage,
  mode,
  previousLabel,
  fromStartDef,
}: {
  stage: FunnelStage;
  mode: FunnelMode;
  previousLabel: string;
  fromStartDef?: string;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2 py-1 pl-[6.5rem] text-xs text-muted-foreground sm:pl-[8.75rem]">
      <ArrowDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {mode === 'activity' ? (
        <span>Different people at each step, so no rate</span>
      ) : (
        <span>
          <RateValue rate={stage.rateFromPrevious} reason={`Nobody ${previousLabel.toLowerCase()} in this period`} /> of{' '}
          {previousLabel.toLowerCase()}
          {stage.rateFromStart != null && previousLabel !== STAGE_LABELS.messaged ? (
            <>
              {' '}
              · <span className="font-mono">{pctText(stage.rateFromStart)}</span>{' '}
              <MetricLabel label="of messaged" def={fromStartDef} />
            </>
          ) : null}
        </span>
      )}
    </div>
  );
}

function DurationTile({ label, def, stats }: { label: string; def?: string; stats: DurationStats }): JSX.Element {
  const thin = isThinSample(stats.n);
  return (
    <Panel>
      <div className="text-[0.8125rem] font-semibold text-muted-foreground">
        <MetricLabel label={label} def={def} />
      </div>
      {stats.n === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">No creators reached this step in this period.</p>
      ) : (
        <>
          <div className={cn('mt-2 flex items-baseline gap-4 font-mono tabular-nums', thin && 'text-muted-foreground/60')}>
            <span>
              <span className="text-[1.5rem] font-medium tracking-[-0.04em]">{daysText(stats.p50)}</span>
              <span className="ml-1 font-sans text-xs text-muted-foreground">typical (median)</span>
            </span>
            <span>
              <span className="text-lg">{daysText(stats.p90)}</span>
              <span className="ml-1 font-sans text-xs text-muted-foreground">slowest 10% take longer</span>
            </span>
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            Across {stats.n.toLocaleString()} creator{stats.n === 1 ? '' : 's'}
            {thin ? `, fewer than ${MIN_SAMPLE}: too few to rely on` : ''}.
          </p>
        </>
      )}
    </Panel>
  );
}

function ChannelTable({
  rows,
  mode,
  def,
  stageDef,
  drill,
}: {
  rows: Funnel2Response['byChannel'];
  mode: FunnelMode;
  def: (key: string) => string | undefined;
  stageDef: (key: FunnelStageKey) => string | undefined;
  drill: (key: FunnelStageKey, extra?: Partial<DrillOptions>) => string | null;
}): JSX.Element {
  const sorted = [...rows].sort((a, b) => b.replied - a.replied);
  return (
    <Section
      title={mode === 'cohort' ? 'Which channels turn replies into posts?' : 'Where did this period’s work happen?'}
      description={
        mode === 'cohort'
          ? 'Post rate is posted ÷ replied for each channel’s replies in this period.'
          : 'Each step counted when it happened in the period.'
      }
    >
      {sorted.length === 0 ? (
        <Panel>
          <p className="text-sm text-muted-foreground">No channel recorded anything in this period.</p>
        </Panel>
      ) : (
        <div className="table-frame relative overflow-x-auto">
          <table className="responsive-stack w-full text-sm" aria-label="By channel">
            <thead>
              <tr className="text-left">
                <Th label="Channel" numeric={false} />
                <Th label="Messaged" def={stageDef('messaged')} />
                <Th label="Replied" def={stageDef('replied')} />
                <Th label="Joined" def={stageDef('joined')} />
                <Th label="Posted" def={stageDef('posted')} />
                {mode === 'cohort' ? <Th label="Post rate" def={def('postRate') ?? 'Posted ÷ replied.'} /> : null}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.channel} className="border-t border-border transition-colors hover:bg-accent/40">
                  <td className="px-3 py-2 font-medium">
                    <span className="inline-flex items-center gap-2">
                      <PlatformIcon platform={r.channel} size={14} className="text-muted-foreground" />
                      {channelLabel(r.channel)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono" data-label="Messaged">
                    {r.messaged.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right" data-label="Replied">
                    <CountLink value={r.replied} to={drill('replied', { channel: r.channel })} />
                  </td>
                  <td className="px-3 py-2 text-right" data-label="Joined">
                    <CountLink value={r.joined} to={drill('joined', { channel: r.channel })} />
                  </td>
                  <td className="px-3 py-2 text-right" data-label="Posted">
                    <CountLink value={r.posted} to={drill('posted', { channel: r.channel })} />
                  </td>
                  {mode === 'cohort' ? (
                    <td className="px-3 py-2 text-right" data-label="Post rate">
                      <RateValue rate={ratio(r.posted, r.replied)} n={r.replied} />
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function CreatorTypeTable({
  rows,
  mode,
  def,
  stageDef,
}: {
  rows: Funnel2Response['byCreatorType'];
  mode: FunnelMode;
  def: (key: string) => string | undefined;
  stageDef: (key: FunnelStageKey) => string | undefined;
}): JSX.Element {
  const sorted = [...rows].sort((a, b) => b.replied - a.replied);
  return (
    <Section title="Which creator types join and post?" description="Creator type is the rate-card tier the invite was logged under.">
      {sorted.length === 0 ? (
        <Panel>
          <p className="text-sm text-muted-foreground">No replies in this period.</p>
        </Panel>
      ) : (
        <div className="table-frame relative overflow-x-auto">
          <table className="responsive-stack w-full text-sm" aria-label="By creator type">
            <thead>
              <tr className="text-left">
                <Th label="Creator type" def={def('creatorType')} numeric={false} />
                <Th label="Replied" def={stageDef('replied')} />
                <Th label="Joined" def={stageDef('joined')} />
                <Th label="Posted" def={stageDef('posted')} />
                <Th label="Join rate" def={def('joinRate') ?? 'Joined ÷ replied, tracked teams only.'} />
                <Th label="Post rate" def={def('postRate') ?? 'Posted ÷ replied, tracked teams only.'} />
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.creatorType} className="border-t border-border transition-colors hover:bg-accent/40">
                  <td className="px-3 py-2 font-medium">{creatorTypeLabel(r.creatorType)}</td>
                  <td className="px-3 py-2 text-right font-mono" data-label="Replied">
                    {r.replied.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right font-mono" data-label="Joined">
                    {r.joined.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right font-mono" data-label="Posted">
                    {r.posted.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right" data-label="Join rate">
                    <RateValue
                      rate={r.joinRate}
                      n={r.replied}
                      reason={mode === 'activity' ? 'No rates in activity mode' : 'No tracked replies of this type'}
                    />
                  </td>
                  <td className="px-3 py-2 text-right" data-label="Post rate">
                    <RateValue
                      rate={r.postRate}
                      n={r.replied}
                      reason={mode === 'activity' ? 'No rates in activity mode' : 'No tracked replies of this type'}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}
